import {
  Delivery,
  PacketType,
  activityAt,
} from 'app/state/ducks/trade-document-delivery/types'

import { formatDocumentTimestamp as fmt } from './timestamp-format'

export const packetDisplayName = (packetType: PacketType | undefined) =>
  packetType === 'open' ? 'Open Trade Packet' : 'Settled Trade Packet'

const COLORS = {
  none: '#9B9B9B',
  prepared: '#32439A',
  sent: '#287A26',
  stale: '#D97706',
  failed: '#DC2626',
}

export interface DeliveryView {
  state: 'none' | 'prepared' | 'sent' | 'stale' | 'prepFailed' | 'sendFailed'
  color: string
  /** Badge character in the top-right circle ('!' stale, '✕' failed). */
  badge: '!' | '✕' | null
  /** Uppercase packet-type label above the icon ('OPEN' / 'SETTLEMENT'). */
  packetLabel: string | null
  label: string | null
  /** Whether clicking the icon opens a popup for this state. */
  clickable: boolean
}

// The Delivery cell shows a single most-current packet (LOS-6471): the record
// most recently acted on, by the shared activityAt ranking (same rule the
// reducer's merge uses, so display and store ordering cannot drift).
export const mostCurrentDelivery = (
  deliveries: Delivery[],
): Delivery | undefined =>
  deliveries.slice().sort((a, b) => activityAt(b) - activityAt(a))[0]

export const deliveryViewFor = (d: Delivery | undefined): DeliveryView => {
  if (!d)
    return {
      state: 'none',
      color: COLORS.none,
      badge: null,
      packetLabel: null,
      label: null,
      clickable: false,
    }
  // Records that predate packetType are settled packets by construction.
  const packetLabel =
    (d.packetType ?? 'settled') === 'open' ? 'OPEN' : 'SETTLEMENT'

  if (d.status === 'PrepFailed')
    return {
      state: 'prepFailed',
      color: COLORS.failed,
      badge: '✕',
      packetLabel,
      label: d.failedAt ? `Prep Failed: ${fmt(d.failedAt)}` : 'Prep Failed',
      clickable: true,
    }
  if (d.status === 'SendFailed')
    return {
      state: 'sendFailed',
      color: COLORS.failed,
      badge: '✕',
      packetLabel,
      label: d.failedAt ? `Send Failed: ${fmt(d.failedAt)}` : 'Send Failed',
      clickable: true,
    }
  if (d.status === 'Sent') {
    // Derived staleness (ADR_LOS-027): only sent Open packets carry staleSince.
    if (d.staleSince)
      return {
        state: 'stale',
        color: COLORS.stale,
        badge: '!',
        packetLabel,
        label: `Stale: ${fmt(d.staleSince)}`,
        clickable: true,
      }
    return {
      state: 'sent',
      color: COLORS.sent,
      badge: null,
      packetLabel,
      label: d.sentAt ? `Sent: ${fmt(d.sentAt)}` : 'Sent',
      clickable: false,
    }
  }
  if (d.status === 'Prepared')
    return {
      state: 'prepared',
      color: COLORS.prepared,
      badge: null,
      packetLabel,
      label: d.preparedAt ? `Prepared: ${fmt(d.preparedAt)}` : 'Prepared',
      clickable: true,
    }
  // Unrecognized status (e.g. a newer DeliveryStatus value from a backend
  // ahead of this frontend): render inert rather than offering a
  // confirm-send for a packet whose state we do not understand.
  return {
    state: 'none',
    color: COLORS.none,
    badge: null,
    packetLabel,
    label: null,
    clickable: false,
  }
}
